import React, { useState } from 'react'
import { Button, Modal, Space, Typography, message, Alert } from 'antd'
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons'
import {
  deleteAllPatientLedgerTransactions,
  deleteAllFollowUpAccounts,
  deleteAllCompensationClaims,
  deleteAllFollowUpHistory,
  deleteAllAccountNotes
} from '../../backend-integration/api'

const { Text, Title } = Typography

const ResetBillingData = ({ onReset }) => {
  const [modalVisible, setModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState([])

  const resetAllData = async () => {
    setLoading(true)
    setProgress([])

    const addProgress = (message, status = 'success') => {
      setProgress((prev) => [...prev, { message, status }])
    }

    try {
      // Delete all patient ledger transactions
      addProgress('Deleting patient ledger transactions...')
      try {
        await deleteAllPatientLedgerTransactions()
        addProgress('✓ Patient ledger transactions deleted', 'success')
      } catch (error) {
        addProgress('✗ Failed to delete patient ledger transactions', 'error')
        console.error('Error deleting patient ledger:', error)
      }

      // Delete all follow-up accounts
      addProgress('Deleting follow-up accounts...')
      try {
        await deleteAllFollowUpAccounts()
        addProgress('✓ Follow-up accounts deleted', 'success')
      } catch (error) {
        addProgress('✗ Failed to delete follow-up accounts', 'error')
        console.error('Error deleting follow-up accounts:', error)
      }

      // Delete all compensation claims
      addProgress('Deleting compensation claims...')
      try {
        await deleteAllCompensationClaims()
        addProgress('✓ Compensation claims deleted', 'success')
      } catch (error) {
        addProgress('✗ Failed to delete compensation claims', 'error')
        console.error('Error deleting compensation claims:', error)
      }

      // Delete all follow-up history
      addProgress('Deleting follow-up history...')
      try {
        await deleteAllFollowUpHistory()
        addProgress('✓ Follow-up history deleted', 'success')
      } catch (error) {
        addProgress('✗ Failed to delete follow-up history', 'error')
        console.error('Error deleting follow-up history:', error)
      }

      // Delete all account notes
      addProgress('Deleting account notes...')
      try {
        await deleteAllAccountNotes()
        addProgress('✓ Account notes deleted', 'success')
      } catch (error) {
        addProgress('✗ Failed to delete account notes', 'error')
        console.error('Error deleting account notes:', error)
      }

      // Note: Invoices are in a different module, so we'll just show a message
      addProgress('Note: Invoice data must be deleted from the Invoices module', 'warning')

      message.success('Billing data reset completed')

      // Call the onReset callback if provided
      if (onReset) {
        setTimeout(() => {
          onReset()
        }, 2000)
      }
    } catch (error) {
      console.error('Error resetting billing data:', error)
      message.error('Failed to reset billing data')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Button danger type='primary' icon={<DeleteOutlined />} onClick={() => setModalVisible(true)} size='large'>
        Reset All Billing Data
      </Button>

      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />
            <Title level={4} style={{ margin: 0 }}>
              Confirm Reset All Billing Data
            </Title>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key='cancel' onClick={() => setModalVisible(false)} disabled={loading}>
            Cancel
          </Button>,
          <Button key='confirm' danger type='primary' loading={loading} onClick={resetAllData}>
            Yes, Delete Everything
          </Button>
        ]}
        width={600}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <Alert
            message='This action cannot be undone!'
            description='All billing-related data will be permanently deleted. This includes:'
            type='error'
            showIcon
          />

          <div>
            <Text strong>The following data will be deleted:</Text>
            <ul>
              <li>All patient ledger transactions (payments, credits, debits, etc.)</li>
              <li>All follow-up accounts</li>
              <li>All compensation claims (COID, FEM, SAPS, RMA)</li>
              <li>All follow-up history records</li>
              <li>All account notes</li>
            </ul>
          </div>

          <Alert
            message='Invoice data'
            description='Invoice data is managed in the Invoices module and must be deleted separately from there.'
            type='warning'
            showIcon
          />

          {progress.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Progress:</Text>
              <div style={{ marginTop: 8 }}>
                {progress.map((item, index) => (
                  <div key={index} style={{ marginBottom: 4 }}>
                    <Text type={item.status === 'error' ? 'danger' : item.status === 'warning' ? 'warning' : 'success'}>
                      {item.message}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Space>
      </Modal>
    </>
  )
}

export default ResetBillingData
